import React, { useRef } from "react";
import * as XLSX from "xlsx";
import { generatePDFFromElement } from "../../../../src/utils/invoiceUtils";

const SurveyReport = ({ 
  isOpen, 
  onClose, 
  selectedSurvey,
  sectionName, 
  className,
  campusName,
  targetAudience
}) => {
  const reportRef = useRef();

  // Generate dummy survey data
  const generateSurveyData = (survey) => {
    const questions = [
      {
        id: 1,
        question: "How satisfied are you with the overall teaching quality?",
        responses: [
          { option: "Very Satisfied", count: 45, percentage: 45 },
          { option: "Satisfied", count: 35, percentage: 35 },
          { option: "Neutral", count: 15, percentage: 15 },
          { option: "Dissatisfied", count: 3, percentage: 3 },
          { option: "Very Dissatisfied", count: 2, percentage: 2 }
        ]
      },
      {
        id: 2,
        question: "How would you rate the school facilities?",
        responses: [
          { option: "Excellent", count: 40, percentage: 40 },
          { option: "Good", count: 38, percentage: 38 },
          { option: "Average", count: 18, percentage: 18 },
          { option: "Poor", count: 3, percentage: 3 },
          { option: "Very Poor", count: 1, percentage: 1 }
        ]
      },
      {
        id: 3,
        question: "How effective is the communication between school and parents?",
        responses: [
          { option: "Very Effective", count: 42, percentage: 42 },
          { option: "Effective", count: 33, percentage: 33 },
          { option: "Moderate", count: 20, percentage: 20 },
          { option: "Ineffective", count: 4, percentage: 4 },
          { option: "Very Ineffective", count: 1, percentage: 1 }
        ]
      },
      {
        id: 4,
        question: "How would you rate the school's safety measures?",
        responses: [
          { option: "Excellent", count: 48, percentage: 48 },
          { option: "Good", count: 35, percentage: 35 },
          { option: "Average", count: 12, percentage: 12 },
          { option: "Poor", count: 4, percentage: 4 },
          { option: "Very Poor", count: 1, percentage: 1 }
        ]
      },
      {
        id: 5,
        question: "How satisfied are you with the extracurricular activities?",
        responses: [
          { option: "Very Satisfied", count: 38, percentage: 38 },
          { option: "Satisfied", count: 40, percentage: 40 },
          { option: "Neutral", count: 17, percentage: 17 },
          { option: "Dissatisfied", count: 4, percentage: 4 },
          { option: "Very Dissatisfied", count: 1, percentage: 1 }
        ]
      }
    ];

    const keyInsights = [
      "Overall satisfaction with teaching quality is high (80% satisfied or very satisfied)",
      "School facilities are well-maintained with 78% rating them as good or excellent",
      "Communication effectiveness shows room for improvement (25% rate it as moderate or below)",
      "Safety measures are highly rated with 83% positive feedback",
      "Extracurricular activities are well-received with 78% satisfaction rate"
    ];

    return {
      ...survey,
      questions,
      keyInsights,
      satisfactionIndex: 78.5, // Overall satisfaction percentage
      totalResponses: 100
    };
  };

  const surveyData = selectedSurvey ? generateSurveyData(selectedSurvey) : null;
  const academicYear = "2023-24";

  const downloadPDF = () => {
    generatePDFFromElement(
      reportRef.current,
      `Survey_Report_${selectedSurvey.title.replace(/\s+/g, '_')}.pdf`
    );
  };

  const downloadExcel = () => {
    if (!surveyData) return;

    const workbook = XLSX.utils.book_new();
    
    // Summary sheet
    const summaryData = [
      ["Survey Report Summary"],
      ["Survey ID", surveyData.id],
      ["Survey Title", surveyData.title],
      ["Date Conducted", surveyData.dateConducted],
      ["Target Audience", surveyData.targetAudience],
      ["Number of Respondents", surveyData.totalResponses],
      ["Satisfaction Index", surveyData.satisfactionIndex + "%"],
      ["Campus", campusName],
      ["Class", className],
      ["Section", sectionName],
      ["Academic Year", academicYear],
      ["Report Date", new Date().toLocaleDateString('en-GB')]
    ];
    
    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");

    // Questions and responses sheet
    const questionsData = [
      ["Questions & Response Summary"],
      ["Survey Title", surveyData.title],
      [],
      ["Question", "Response Option", "Count", "Percentage"]
    ];

    surveyData.questions.forEach(question => {
      question.responses.forEach(response => {
        questionsData.push([
          question.question,
          response.option,
          response.count,
          response.percentage + "%"
        ]);
      });
      questionsData.push([]); // Empty row between questions
    });

    const questionsSheet = XLSX.utils.aoa_to_sheet(questionsData);
    XLSX.utils.book_append_sheet(workbook, questionsSheet, "Questions & Responses");

    // Key insights sheet
    const insightsData = [
      ["Key Insights"],
      ["Survey Title", surveyData.title],
      [],
      ["Insight"]
    ];

    surveyData.keyInsights.forEach(insight => {
      insightsData.push([insight]);
    });

    const insightsSheet = XLSX.utils.aoa_to_sheet(insightsData);
    XLSX.utils.book_append_sheet(workbook, insightsSheet, "Key Insights");
    
    XLSX.writeFile(workbook, `Survey_Report_${selectedSurvey.title.replace(/\s+/g, '_')}.xlsx`);
  };

  if (!isOpen || !surveyData) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
      <div ref={reportRef} className="bg-white p-6 rounded-lg shadow-lg w-[90%] max-h-[90vh] overflow-y-auto relative border border-gray-300">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-xl font-bold text-gray-800">{campusName}</h1>
          <h2 className="text-lg font-semibold mt-2">Survey Report</h2>
          <p className="text-base">{academicYear}</p>
          <p className="text-base font-medium">{className} - {sectionName}</p>
          <p className="text-sm text-gray-600">Report Generated: {new Date().toLocaleDateString('en-GB')}</p>
        </div>

        {/* Survey Information */}
        <div className="mb-6">
          <div className="grid grid-cols-1 gap-y-2">
            <div className="flex">
              <p className="w-32 font-semibold">Survey ID</p>
              <p className="flex-1 border-b border-gray-400 ml-4">{surveyData.id}</p>
            </div>
            <div className="flex">
              <p className="w-32 font-semibold">Survey Title</p>
              <p className="flex-1 border-b border-gray-400 ml-4">{surveyData.title}</p>
            </div>
            <div className="flex">
              <p className="w-32 font-semibold">Date Conducted</p>
              <p className="w-32 border-b border-gray-400 ml-4">{surveyData.dateConducted}</p>
              <p className="w-16 font-semibold ml-8">Target</p>
              <p className="w-32 border-b border-gray-400 ml-4">{surveyData.targetAudience}</p>
            </div>
            <div className="flex">
              <p className="w-32 font-semibold">Respondents</p>
              <p className="w-32 border-b border-gray-400 ml-4">{surveyData.totalResponses}</p>
              <p className="w-16 font-semibold ml-8">Satisfaction</p>
              <p className="w-32 border-b border-gray-400 ml-4">
                <span className={`font-semibold ${
                  surveyData.satisfactionIndex >= 80 ? 'text-green-600' :
                  surveyData.satisfactionIndex >= 60 ? 'text-yellow-600' :
                  'text-red-600'
                }`}>
                  {surveyData.satisfactionIndex}%
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Satisfaction Index */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4 text-center">Overall Satisfaction Index</h3>
          <div className="border border-gray-400 p-6 text-center">
            <div className="text-4xl font-bold mb-2">
              <span className={`${
                surveyData.satisfactionIndex >= 80 ? 'text-green-600' :
                surveyData.satisfactionIndex >= 60 ? 'text-yellow-600' :
                'text-red-600'
              }`}>
                {surveyData.satisfactionIndex}%
              </span>
            </div>
            <p className="text-lg text-gray-600">Overall Satisfaction Rating</p>
            <div className="mt-4">
              <div className="w-full bg-gray-200 rounded-full h-4">
                <div 
                  className={`h-4 rounded-full ${
                    surveyData.satisfactionIndex >= 80 ? 'bg-green-500' :
                    surveyData.satisfactionIndex >= 60 ? 'bg-yellow-500' :
                    'bg-red-500'
                  }`}
                  style={{ width: `${surveyData.satisfactionIndex}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        {/* Questions & Response Summary */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4 text-center">Questions & Response Summary</h3>
          <div className="space-y-6">
            {surveyData.questions.map((question, questionIndex) => (
              <div key={questionIndex} className="border border-gray-400 p-4">
                <h4 className="font-semibold mb-3 text-gray-700">{question.id}. {question.question}</h4>
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-400 p-2 text-left">Response Option</th>
                      <th className="border border-gray-400 p-2 text-center">Count</th>
                      <th className="border border-gray-400 p-2 text-center">Percentage</th>
                      <th className="border border-gray-400 p-2 text-center">Visual</th>
                    </tr>
                  </thead>
                  <tbody>
                    {question.responses.map((response, responseIndex) => (
                      <tr key={responseIndex}>
                        <td className="border border-gray-400 p-2">{response.option}</td>
                        <td className="border border-gray-400 p-2 text-center">{response.count}</td>
                        <td className="border border-gray-400 p-2 text-center">{response.percentage}%</td>
                        <td className="border border-gray-400 p-2">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-blue-500 h-2 rounded-full"
                              style={{ width: `${response.percentage}%` }}
                            ></div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </div>

        {/* Key Insights */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4 text-center">Key Insights</h3>
          <div className="border border-gray-400 p-4">
            <ul className="space-y-3">
              {surveyData.keyInsights.map((insight, index) => (
                <li key={index} className="flex items-start">
                  <span className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-semibold mr-3 mt-0.5">
                    {index + 1}
                  </span>
                  <span className="text-gray-700">{insight}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Survey Statistics */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4 text-center">Survey Statistics</h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="border border-gray-400 p-4 text-center">
              <h4 className="font-semibold mb-2">Total Questions</h4>
              <div className="text-2xl font-bold text-blue-600">{surveyData.questions.length}</div>
            </div>
            <div className="border border-gray-400 p-4 text-center">
              <h4 className="font-semibold mb-2">Total Responses</h4>
              <div className="text-2xl font-bold text-green-600">{surveyData.totalResponses}</div>
            </div>
            <div className="border border-gray-400 p-4 text-center">
              <h4 className="font-semibold mb-2">Response Rate</h4>
              <div className="text-2xl font-bold text-purple-600">95%</div>
            </div>
          </div>
        </div>

        {/* Signature Lines */}
        <div className="grid grid-cols-3 gap-8 mt-12">
          <div className="text-center">
            <div className="border-t border-gray-400 mt-8 pt-2">
              Survey Coordinator's Signature
            </div>
          </div>
          <div className="text-center">
            <div className="border-t border-gray-400 mt-8 pt-2">
              Head of Department's Signature
            </div>
          </div>
          <div className="text-center">
            <div className="border-t border-gray-400 mt-8 pt-2">
              Principal's Signature
            </div>
          </div>
        </div>

        {/* Download Buttons */}
        <div className="no-print flex justify-end mt-6 space-x-4">
          <button onClick={downloadPDF} className="bg-blue-500 px-4 py-2 rounded text-white hover:bg-blue-600">
            Download as PDF
          </button>
          <button onClick={downloadExcel} className="bg-green-500 px-4 py-2 rounded text-white hover:bg-green-600">
            Download as Excel
          </button>
          <button onClick={onClose} className="bg-gray-400 px-4 py-2 rounded text-white hover:bg-gray-500">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SurveyReport;
